import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { debounceTime } from 'rxjs/operators';

import { Client } from 'src/providers/client/client';
import { Loading } from 'src/shared/components/loading/loading';
import { Genome } from 'src/providers/genome/genome';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { PhotoNotes } from 'src/providers/photo-notes/photo-notes';
import { PhotoCaptionService } from 'src/shared/components/photo-caption/photo-caption.service';
import { Util } from 'src/shared/util/util';
import { AssetsQueriesService } from 'src/shared/services/assets-queries.service';
import { v4 as uuidv4 } from 'uuid';

import { floorSelect } from 'src/shared/constants/floorSelect';
import { Capacitor } from '@capacitor/core';
import { DateTime as LuxonDateTime } from 'luxon';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';

@Component({
    templateUrl: 'add-room-item.html',
    standalone: false
})

export class AddRoomItemPage implements OnInit {
  private readonly assetsQueries = inject(AssetsQueriesService);

  addressItems: any;
  category: any;
  dialogParams: any;
  installationDateItems: any;
  form: UntypedFormGroup;
  floorSelectItems = floorSelect;
  submitted: boolean;
  categories: any;
  categoryChosen: boolean;
  categoriesResponse: any;
  characteristics: { name: String, formData: { value: number, viewValue: String } }[];
  characteristicsChosen: { [id: string]: number } = {};
  hasNoItemsOrMap: boolean;
  loaded: boolean;
  models: any = [];
  isRightSideContent: boolean;
  getInventoryAlerts: boolean;
  createNewModel: boolean;
  characteristicsArray: any = [];
  errorMessage: string;
  salesCards: any;
  selectedModelID: number;
  showAddressSelect: boolean;
  showConsumables: boolean;
  deliveryTimingPreferenceItems = [
    { value: 'Between guest stays', viewValue: new TranslationPipe().transform('Between guest stays') },
    { value: 'As soon as possible', viewValue: new TranslationPipe().transform('As soon as possible') }
  ];
  photoURL: string;
  selectedCategories: any[] = [];
  existingCategoryIds: Set<number> = new Set();
  isSavingBulk = false;
  allPropertiesSelected = false;
  commonAssetNames = [
    'Refrigerator', 'Oven', 'Dishwasher', 'Washing Machine',
    'Dryer', 'Water Heater', 'Air Conditioner', 'Heater'
  ];

  boundBeginMapping = this.beginMapping.bind(this);
  boundDontScanHome = this.dontScanHome.bind(this);
  boundAddItem = this.addItem.bind(this);
  boundSaveSelectedAssets = this.saveSelectedAssets.bind(this);

constructor(
    private client: Client,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private navCtrl: CustomNavController,
    private rightSidePanelService: RightSidePanelService,
    private storage: TidyStorage,
    private photoNotes: PhotoNotes,
    private photoCaptionService: PhotoCaptionService,
    private util: Util
  ) {
    this.form = this.fb.group({
      search: [''],
      characteristic: [''],
      model: ['', Validators.required],
      installation_date: [''],
      minQuantity: [''],
      maxQuantity: [''],
      currentQuantity: [''],
      opportunisticOrderLevel: [''],
      minRestockingPrice: [''],
      maxRestockingPrice: [''],
      whereToOrder: [''],
      deliveryTimingPreferences: [''],
      restockingNotes: [''],
      address: [[], Validators.required]
    });
    this.form.valueChanges.pipe(
      debounceTime(300)
    ).subscribe(val => this.updateSearch(val));
    this.form.get('model')?.valueChanges.pipe(
      debounceTime(100)
    ).subscribe(val => {
      if (val === '' || val === null) {
        this.selectedModelID = null;
      } else if (val && typeof val === 'string') {
        const matchingOption = this.models.find(option => option.display === val);
        if (!matchingOption) {
          this.selectedModelID = null;
        }
      }
    });
  }

  @Loading('', true)
  async ngOnInit() {
    try {
      this.loaded = false;
      this.categoryChosen = false;
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      if (this.isRightSideContent) {
        this.dialogParams = await this.storage.retrieve('dialog-params');
        this.showConsumables = this.dialogParams?.showConsumables;
        this.rightSidePanelService.setDialogPageTitle(this.showConsumables ? 'Add Item' : 'Add Asset');
        this.hasNoItemsOrMap = this.dialogParams?.hasNoItemsOrMap;
        this.showAddressSelect = this.dialogParams?.showAddressSelect;
      } else {
        this.showConsumables = this.navCtrl.getParam('showConsumables');
        this.hasNoItemsOrMap = this.navCtrl.getParam('hasNoItemsOrMap');
        this.showAddressSelect = this.navCtrl.getParam('showAddressSelect');
      }
      this.categoriesResponse = await this.genome.getCategories();
      this.categoriesResponse = this.categoriesResponse.filter((category) => category.consumable === this.showConsumables);
      if (!this.showConsumables) {
        await this.loadExistingAssets();
        this.initializeDefaultSelections();
      }
      this.categoriesResponse = this.sortCategories();
      this.createAddressForm();
      this.categories = this.categoriesResponse;
      this.installationDateItems = this.buildInstallationDateItems();
      this.salesCards = this.getSalesCards();
      this.setValidators();
      this.loaded = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  sortCategories() {
    if (this.showConsumables) {
      const priorityOrder = [
        'Toilet Paper',
        'Paper Towels',
        'Dish Soap',
        'Hand Soap',
        'Laundry Detergent',
        'Dishwasher Detergent',
        'Trash Bags',
        'Coffee',
        'Coffee Filters',
        'Shampoo',
        'Conditioner',
        'Body Wash'
      ];
      return this.categoriesResponse.sort((a, b) => {
        const indexA = priorityOrder.indexOf(a.name);
        const indexB = priorityOrder.indexOf(b.name);
        if (indexA === -1 && indexB === -1) { return 0; }
        if (indexA === -1) { return 1; }
        if (indexB === -1) { return -1; }
        return indexA - indexB;
      });
    }
    return this.categoriesResponse.sort((a, b) => {
      const aSelected = this.isCategorySelected(a);
      const bSelected = this.isCategorySelected(b);
      if (aSelected && !bSelected) {return -1;}
      if (!aSelected && bSelected) {return 1;}
      const aCommon = this.commonAssetNames.includes(a.name);
      const bCommon = this.commonAssetNames.includes(b.name);
      if (aCommon && !bCommon) {return -1;}
      if (!aCommon && bCommon) {return 1;}
      return 0;
    });
  }

  async loadExistingAssets(): Promise<void> {
    try {
      const response = await this.assetsQueries.getAssets({ consumable: false, perPage: 500 });
      const existingAssets = response.body || [];
      this.existingCategoryIds = new Set(
        existingAssets
          .filter(asset => asset.model?.category?.id)
          .map(asset => asset.model.category.id)
      );
    } catch (_err) {
      this.existingCategoryIds = new Set();
    }
  }

  initializeDefaultSelections(): void {
    this.selectedCategories = this.categoriesResponse.filter(category =>
      this.commonAssetNames.includes(category.name) &&
      !this.existingCategoryIds.has(category.id)
    );
  }

  toggleCategorySelection(category: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    const index = this.selectedCategories.findIndex(c => c.id === category.id);
    if (index > -1) {
      this.selectedCategories.splice(index, 1);
    } else {
      this.selectedCategories.push(category);
    }
  }

  isCategorySelected(category: any): boolean {
    return this.selectedCategories.some(c => c.id === category.id);
  }

  async saveSelectedAssets(): Promise<void> {
    if (this.selectedCategories.length === 0 || this.isSavingBulk) {
      return;
    }
    this.submitted = true;
    const addressIds = this.form.value.address || [];
    if (addressIds.length === 0) {
      return;
    }
    this.isSavingBulk = true;
    try {
      const payload = {
        address_ids: addressIds,
        room_objects: this.selectedCategories.map(category => ({
          category_id: category.id
        }))
      };
      await this.genome.createManyRoomObjects(payload);
      this.assetsQueries.invalidateAssets();
      this.util.showSuccess(`${this.selectedCategories.length} assets added successfully`);
      if (Capacitor.isNativePlatform()) {
        this.navCtrl.back();
      } else {
        const cameFromRoute = this.navCtrl.getParam('cameFromRoute') || this.dialogParams?.cameFromRoute;
        if (cameFromRoute) {
          this.rightSidePanelService.navigateTo(cameFromRoute);
        } else {
          this.navCtrl.navigateForward('assets');
        }
      }
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
      this.util.showError('Error adding assets. Please try again.');
    } finally {
      this.isSavingBulk = false;
    }
  }

  @Loading('', true)
  async selectCategory(category) {
    const params = {
      category,
      categories: this.categories,
      showConsumables: this.showConsumables,
      showAddressSelect: this.showAddressSelect,
      // Clear any item left in dialog-params by a prior edit so navigateTo's
      // param merge doesn't put us into "Update Item" mode when adding.
      item: null
    }
    this.rightSidePanelService.navigateTo('add-item-form', params);
  }

  async createAddressForm() {
    try {
      const getAddressesFromBackend = false;
      const addresses = await this.client.getMoreDetailAddresses(getAddressesFromBackend);
      const includeAddAddress = false;
      this.addressItems = this.client.parseAddressList(addresses, includeAddAddress);
      const allValues = this.addressItems.map(item => item.value);
      this.form.patchValue({
        address: allValues
      });
      this.allPropertiesSelected = true;
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  toggleSelectAllProperties(): void {
    if (this.allPropertiesSelected) {
      this.form.patchValue({ address: [] });
      this.allPropertiesSelected = false;
    } else {
      const allValues = this.addressItems.map(item => item.value);
      this.form.patchValue({ address: allValues });
      this.allPropertiesSelected = true;
    }
  }

  @Loading('', true)
  selectCharacteristic(characteristic, id) {
    try {
      this.characteristicsChosen[characteristic.name] = id;
      const query = this.buildQueryString(this.characteristicsChosen);
      this.getModels(query);
    } catch (_err) {
      // Silent error handling
    }
  }

  @Loading('', true)
  async getModels(query) {
    const models = await this.genome.getObjectModel(this.category.id, query);
    this.models = models.map((model) => {
      const formData = {
        value: model.id,
        display: model.model
      };
      return formData;
    });
  }

  selectModel(model) {
    this.selectedModelID = model.value;
  }

  buildQueryString(obj) {
    let finalQuery = '';
    const finalArray = [];
    for (const key in obj) {
      if (obj[key] !== -1) {
        finalQuery += `&filters[characteristic_ids][]=${obj[key]}`;
        finalArray.push(obj[key]);
      }
    }
    this.characteristicsArray = finalArray;
    return finalQuery;
  }

  async addItem() {
    this.submitted = true;
    this.errorMessage = '';
    if (!this.form.valid) {
      return;
    }
    const data = {
      category_id: this.category.id,
      address_id: this.form.value.address,
      installation_date: `01/01/${this.form.value.installation_date}`,
      model_id: this.form.value.model,
    }
    if (this.photoURL) {
      data['photo_url'] = await this.uploadPhoto();
    }
    if (this.showConsumables) {
      data['min_quantity'] = this.form.value.minQuantity;
      data['current_quantity'] = this.form.value.currentQuantity ? this.form.value.currentQuantity : null;
      data['max_quantity'] = this.form.value.maxQuantity ? this.form.value.maxQuantity : null;
      data['opportunistic_quantity'] = this.form.value.opportunisticOrderLevel ? this.form.value.opportunisticOrderLevel : null;
      data['delivery_timing_preference'] = this.form.value.deliveryTimingPreferences ? this.form.value.deliveryTimingPreferences : null;
      data['restocking_notes'] = this.form.value.restockingNotes ? this.form.value.restockingNotes : null;
      data['where_to_order'] = this.form.value.whereToOrder ? this.form.value.whereToOrder : null;
      data['min_restocking_price'] = this.form.value.minRestockingPrice ? parseFloat(this.form.value.minRestockingPrice.replace('$', '')) : null;
      data['max_restocking_price'] = this.form.value.maxRestockingPrice ? parseFloat(this.form.value.maxRestockingPrice.replace('$', '')) : null;
    } else {
      data['min_quantity'] = null;
      data['current_quantity'] = null;
      data['max_quantity'] = null;
      data['delivery_timing_preference'] = null;
      data['restocking_notes'] = null;
      data['where_to_order'] = null;
      data['min_restocking_price'] = null;
      data['max_restocking_price'] = null;
      data['opportunistic_quantity'] = null;
    }
    try {
      if (!this.selectedModelID) {
        const modelData = {
          category_id: this.category.id,
          model: this.form.value.model,
          name: this.form.value.model,
          characteristic_ids: this.characteristicsArray
        }
        const newModel = await this.genome.addNewModel(modelData);
        data.model_id = newModel.id;
      }
      await this.genome.addRoomObject(data);
      this.assetsQueries.invalidateAssets();
      if (Capacitor.isNativePlatform()) {
        this.navCtrl.back();
      } else {
        const cameFromRoute = this.navCtrl.getParam('cameFromRoute') || this.dialogParams.cameFromRoute;
        if (cameFromRoute) {
          this.rightSidePanelService.navigateTo(cameFromRoute);
        } else if (this.showConsumables) {
          this.navCtrl.navigateForward('restocking');
        } else {
          this.navCtrl.navigateForward('assets');
        }
      }
    } catch (err) {
      this.errorMessage = err.error ? err.error.message : err.message;
    }
  }

  async uploadPhoto() {
    const uuid = uuidv4();
    const photo = await this.photoNotes.uploadPictureToS3(this.photoURL , `item/${uuid}`);
    return photo.Location;
  }

  buildInstallationDateItems() {
    const array: any = [];
    const year = LuxonDateTime.now().year;
    for (let i = year; i > (year - 232); i--) {
      array.push({
        value: i,
        viewValue: i
      });
    }
    return array;
  }

  getSalesCards() {
    return [
      {
        title: 'Get Maintenance Tips',
        text: 'When you add an item, we will tell you what the manufacturer recommends for that.',
        image: 'assets/img/tool-box.svg'
      },
      {
        title: 'Clean Smarter',
        text: 'Knowing what is in your home helps you and any pros you use follow best practices for that exact thing.',
        image: 'assets/img/light-bulb.svg'
      },
      {
        title: 'Track Maintenance',
        text: 'Tracking what was done makes it easier for future maintenance and repairs. It\'s like a medical record of your home!',
        image: 'assets/img/task-list.svg'
      }
    ];
  }

  dontScanHome() {
    this.hasNoItemsOrMap = false;
  }

  beginMapping() {
    const params = {
      address: this.navCtrl.getParam('address') || this.dialogParams?.address,
      floors: this.navCtrl.getParam('floors') || this.dialogParams?.floors,
      addressName: this.navCtrl.getParam('addressName') || this.dialogParams?.addressName
    };
    this.navCtrl.navigateForward(`map-introduction`, params);
  }

  setValidators() {
    if (this.showConsumables) {
      // A consumable can't be restocked without a reorder trigger (min), a target
      // level to top up to (max) and a price ceiling for the concierge (max price).
      this.form.controls.minQuantity.setValidators([Validators.required]);
      this.form.controls.maxQuantity.setValidators([Validators.required]);
      this.form.controls.maxRestockingPrice.setValidators([Validators.required]);
      this.form.controls.maxQuantity.updateValueAndValidity();
      this.form.controls.maxRestockingPrice.updateValueAndValidity();
    } else {
      this.form.controls.minQuantity.clearValidators();
      this.form.controls.minQuantity.updateValueAndValidity();
      this.form.controls.maxQuantity.clearValidators();
      this.form.controls.maxQuantity.updateValueAndValidity();
      this.form.controls.maxRestockingPrice.clearValidators();
      this.form.controls.maxRestockingPrice.updateValueAndValidity();
      this.form.patchValue({
        deliveryTimingPreferences: null,
        opportunisticOrderLevel: null,
        whereToOrder: null,
        minRestockingPrice: null,
        maxRestockingPrice: null,
        restockingNotes: null,
        minQuantity: null,
        maxQuantity: null,
        currentQuantity: null
      });
    }
  }

  updateSearch({search}) {
    if (search.length < 2) {
      return this.categories = this.categoriesResponse;
    }
    const term = search.toLowerCase();
    this.categories = this.categoriesResponse.filter((option) => {
      if (option.name.toLowerCase().indexOf(term) > -1) {
        return option;
      }
    });
  }

  goToAddressPage(addressId) {
    this.navCtrl.navigateForward(`properties?propertyId=${addressId}`)
  }

  goToAddConciergeTask() {
    const params = {
      title: 'Concierge Action',
      type: 'support.concierge_task',
      isAddingRoomItem: true
    }
    this.rightSidePanelService.navigateTo('contact-concierge', params);
  }

  async addImage() {
    try {
      const newPhotoNote = await this.photoCaptionService.getImg('task');
      this.photoURL = newPhotoNote.url;
    } catch (err) {
      this.util.showError('Error uploading photo. Please try again.', err);
    }
  }

  removeImage(_event) {
    this.photoURL = null;
  }

}
